/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import type { AxiosRequestConfig } from 'axios';
import axios from 'axios';
import { Cookies } from 'react-cookie';

import { CONFIG_API_DOMAIN, CONFIG_TIKTOK_API_DOMAIN, getStringConfig } from '@/hooks/uesAppConfig';
import { EAPP_CHANNEL_VALUE, getAppChannelValue, isDouyinAppChannel } from '@/channels/code';
import { channelNavigateTo } from '@/channels/navigate';
import { HOME } from '@/constants/path';
import { getShuMeiSDKDeviceId } from '@/utils/shumei';
import { getSignV4Func } from '@/utils/sign-v4';
import { ampHandler, ampHandlerHelpers } from '@/utils/elk';

const APP_REQUEST_GROUP = {
  [EAPP_CHANNEL_VALUE.DOUYIN]: {
    ct: 38,
    channelCode: 110,
    apiVersion: '6.0.35.0',
  },
  [EAPP_CHANNEL_VALUE.UNIONPAY]: {
    ct: 37,
    channelCode: 96,
    apiVersion: '6.0.35.0',
  },
  // 暂无单独 H5 渠道, 主要用于测试, 暂时使用 unionpay API 通道
  [EAPP_CHANNEL_VALUE.H5]: {
    ct: 37,
    channelCode: 96,
    apiVersion: '6.0.35.0',
  },
};

const appRequestConfigValue = APP_REQUEST_GROUP[getAppChannelValue()];

export const REQUEST_HEADER_CT = process.env.DEV_CT_WX
  ? '31'
  : process.env.REACT_APP_API_HEADER_CT || appRequestConfigValue.ct;
export const REQUEST_CHANNEL_CODE = process.env.DEV_CT_WX
  ? '45'
  : process.env.REACT_APP_API_CHANNEL_CODE || appRequestConfigValue.channelCode;
export const REQUEST_HEADER_VERSION = process.env.REACT_APP_API_VERSION || appRequestConfigValue.apiVersion;

export type CommonResponse<T = any> = {
  code: number;
  data?: T | null;
  datetime: string;
  message: string;
  success: boolean;
  traceId: string | null;
};

export const getBaseUrl = () => {
  // 同源模式：请求打到当前页面域名下的 /bff，由 nginx / dev-server 反向代理到真实网关。
  // 好处：不产生跨域预检、Cookie 是第一方、手机或模拟器只要能打开页面就能调接口。
  if (process.env.REACT_APP_API_SAME_ORIGIN === 'true') return '';

  switch (process.env.APP_DEPLOY_ENV) {
    case 'prod':
      return getStringConfig(CONFIG_API_DOMAIN, 'https://api.mcd.cn');
    case 'uat':
      return getStringConfig(CONFIG_API_DOMAIN, 'https://api-uat.mcdchina.net');
    case 'sit':
      return getStringConfig(CONFIG_API_DOMAIN, 'https://api-sit.mcdchina.net');
    case 'dev':
      return getStringConfig(CONFIG_API_DOMAIN, 'https://api-dev.mcdchina.net');
    default:
      return getStringConfig(CONFIG_API_DOMAIN, 'https://api.mcd.cn');
  }
};

export const getTiktokUrl = () => {
  switch (process.env.APP_DEPLOY_ENV) {
    case 'prod':
      return getStringConfig(CONFIG_TIKTOK_API_DOMAIN, 'https://tiktok-prod.mcdonalds.cn');
    case 'uat':
      return getStringConfig(CONFIG_TIKTOK_API_DOMAIN, 'https://tiktok-uat.mcdchina.net');
    case 'sit':
      return getStringConfig(CONFIG_TIKTOK_API_DOMAIN, 'https://tiktok-uat.mcdchina.net');
    case 'dev':
      return getStringConfig(CONFIG_TIKTOK_API_DOMAIN, 'https://tiktok-uat.mcdchina.net');
    default:
      return getStringConfig(CONFIG_TIKTOK_API_DOMAIN, 'https://tiktok-prod.mcdonalds.cn');
  }
};

const requestCtx = axios.create({
  baseURL: getBaseUrl(),
  withCredentials: true,
});

const defaultHeaders: AxiosRequestConfig['headers'] = {
  ct: REQUEST_HEADER_CT,
  v: REQUEST_HEADER_VERSION,
};

/**
 * 每次实时读取 cookie 里的 sid，不做模块级缓存。
 * 原因：登录成功后 TransitPage 会用 setCookie 写入新的 sid，
 * 若这里缓存旧值，登录后的请求会继续携带旧/空 sid，导致接口 401 又被清掉。
 */
const getSid = () => (new Cookies().get('sid') as string) || '';

/**
 * 接口返回 401 时：清空本地 sid，退出登录到首页，重新走登录逻辑。
 * 只有「带 sid 发出的请求」返回 401 才处理（匿名请求的 401 是正常未登录，不能误触发）。
 */
let _unauthorizedHandled = false;
const handleUnauthorized = () => {
  if (_unauthorizedHandled) return;
  _unauthorizedHandled = true;

  try {
    const cookies = new Cookies();
    cookies.remove('sid', { path: '/' });
    cookies.remove('meddyId', { path: '/' });
  } catch (error) {
    // ignore
  }

  if (isDouyinAppChannel()) {
    // 抖音：走小程序 logout 页，清掉小程序 storage 里的登录态并回到 H5 首页
    channelNavigateTo(`/pages/logout/index?redirectUrl=${encodeURIComponent(HOME)}`, {
      redirect: true,
    });
  } else {
    window.location.replace(HOME);
  }
};

export const objValueNullToEmptyString = <T>(obj: T, nullString?: boolean): T => {
  if (nullString && obj === null) {
    return '' as unknown as T;
  }
  if (typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return obj.map((i) => objValueNullToEmptyString(i, nullString)) as unknown as T;
  }

  const result = {} as T;
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      if (obj[key] === null && !nullString) {
        continue;
      }
      if (obj[key] !== undefined) {
        result[key] = objValueNullToEmptyString(obj[key]);
      }
    }
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  if (Object.keys(result as any).length === 0) return '' as any;

  return result;
};

requestCtx.interceptors.request.use(
  (v) => {
    const { method, headers, params, ...rest } = v;
    const _deviceid: string = getShuMeiSDKDeviceId() || '';

    const _hasData = ['post', 'put', 'patch'].includes(method?.toLocaleLowerCase() || '');
    const _body = _hasData ? { data: params } : { params };
    const _reqBody = {
      method,
      ..._body,
      ...rest,
      headers: {
        ...defaultHeaders,
        'request-timestamp': Date.now(),
        'device-id': _deviceid,
        sid: getSid() ?? '',
        ...headers,
      },
    };

    const _data = objValueNullToEmptyString(_reqBody.params || _reqBody.data, _hasData);
    const _fullUrl = `${v.baseURL || ''}${v.url || ''}`;
    /**
     * 同源代理模式下 _fullUrl 是相对路径（/bff/...），没有域名。
     * 签名器按「实际请求域名」选 AK/SK，这里给一个仅用于选密钥的域名提示，
     * 不能把提示域名拿去参与签名（签名只依赖 path，host 不影响 digest）。
     */
    const _signHostHint = _fullUrl.startsWith('/') ? `${process.env.REACT_APP_SIGN_HOST || ''}${_fullUrl}` : _fullUrl;
    const _reqV4 = getSignV4Func(_signHostHint)({
      data: _data,
      header: { ..._reqBody.headers, sv: 'v4', tid: '', xid: '', meddyid: '' },
      method,
      url: _fullUrl,
    }) as { header: Record<string, string> };
    return {
      ..._reqBody,
      data: _hasData ? _data : undefined,
      headers: {
        ..._reqBody.headers,
        ..._reqV4?.header,
      },
    };
  },
  (reqErr) => {
    try {
      ampHandler.uploadPerformanceInfo(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        ampHandlerHelpers.getRequestData(reqErr?.config),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        ampHandlerHelpers.getErrorData(reqErr) as any,
      );
    } catch (error) {
      console.error('ampHandler uploadPerformanceInfo error', error);
    }

    return reqErr;
  },
);

requestCtx.interceptors.response.use(
  (res) => {
    try {
      ampHandler.uploadPerformanceInfo(
        ampHandlerHelpers.getRequestData(res.config),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        ampHandlerHelpers.getReponseData(res) as any,
      );
    } catch (error) {
      console.error('ampHandler uploadPerformanceInfo error', error);
    }

    // 业务 401（HTTP 200 + body.code 401）：清 sid 回首页
    if (Number((res?.data as { code?: number } | null)?.code) === 401 && getSid()) {
      console.warn(
        '[401] 业务401 触发清登录态',
        res?.config?.method?.toUpperCase(),
        res?.config?.url,
        (res?.data as { message?: string } | null)?.message,
      );
      handleUnauthorized();
    }

    return res.data;
  },
  (reqErr) => {
    try {
      ampHandler.uploadPerformanceInfo(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        ampHandlerHelpers.getRequestData(reqErr?.config),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        ampHandlerHelpers.getErrorData(reqErr) as any,
      );
    } catch (error) {
      console.error('ampHandler uploadPerformanceInfo error', error);
    }

    // HTTP 401：清 sid 回首页
    if ((reqErr as { response?: { status?: number } } | null)?.response?.status === 401 && getSid()) {
      console.warn(
        '[401] HTTP401 触发清登录态',
        (reqErr as { config?: { method?: string; url?: string } })?.config?.method?.toUpperCase(),
        (reqErr as { config?: { method?: string; url?: string } })?.config?.url,
      );
      handleUnauthorized();
    }

    return reqErr;
  },
);

export type MyAxiosRequestConfig<D = any> = AxiosRequestConfig<D> & {
  pathVars?: any;
};

export interface MyAxios {
  <T = any, D = any>(config: MyAxiosRequestConfig<D>): Promise<T>;
  request<T = any, D = any>(config: MyAxiosRequestConfig<D>): Promise<T>;
  get<T = any, D = any>(url: string, config?: MyAxiosRequestConfig<D>): Promise<T>;
  delete<T = any, D = any>(url: string, config?: MyAxiosRequestConfig<D>): Promise<T>;
  head<T = any, D = any>(url: string, config?: MyAxiosRequestConfig<D>): Promise<T>;
  options<T = any, D = any>(url: string, config?: MyAxiosRequestConfig<D>): Promise<T>;
  post<T = any, D = any>(url: string, config?: MyAxiosRequestConfig<D>): Promise<T>;
  put<T = any, D = any>(url: string, config?: MyAxiosRequestConfig<D>): Promise<T>;
  patch<T = any, D = any>(url: string, config?: MyAxiosRequestConfig<D>): Promise<T>;
}

// [PATCH] post, put, patch args 兼容历史传参
Object.defineProperties(requestCtx, {
  post: {
    value: (url: string, data?: MyAxiosRequestConfig) => requestCtx({ method: 'post', url, ...data }),
  },
  put: {
    value: (url: string, data?: MyAxiosRequestConfig) => requestCtx({ method: 'put', url, ...data }),
  },
  patch: {
    value: (url: string, data?: MyAxiosRequestConfig) => requestCtx({ method: 'patch', url, ...data }),
  },
});

export default requestCtx as MyAxios;
